import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLoading } from "../../contexts/LoadingContext";

// Full-screen overlay that shows on every route change.
// Hides after a minimum delay and when there are no pending loading tasks.
const RouteChangeLoader = ({ minDuration = 1000, exitDuration = 600 }) => {
  const location = useLocation();
  const { pendingCount } = useLoading();
  const [visible, setVisible] = useState(true); // mounted
  const [phase, setPhase] = useState("entering"); // entering | idle | exiting
  const navKeyRef = useRef(location.key);
  const sinceRef = useRef(Date.now());
  const prevLocationRef = useRef(location);

  // Helper to decide if current path is the Shop page (normalize trailing slash)
  const isShopPath = (pathname = "") => {
    const p = pathname.replace(/\/+$/, "").toLowerCase();
    return p.endsWith("/shop");
  };

  // Helper to detect product detail routes (e.g., /product/:id or variants like /product-tab-left/:id)
  const isProductPath = (pathname = "") => {
    const p = pathname.replace(/\/+$/, "").toLowerCase();
    // Matches /product, /product/..., /product-.../... variants
    return /\/(product($|\/)|product-[^/]+\/)/.test(p + "/");
  };

  // Start overlay on route change
  useEffect(() => {
    if (location.key !== navKeyRef.current) {
      // Determine if this navigation is an in-shop browse (only the query string changed)
      const prev = prevLocationRef.current;
  const inShopBrowse =
        prev &&
        prev.pathname === location.pathname &&
        isShopPath(location.pathname) &&
        prev.search !== location.search;
  const openingProduct = isProductPath(location.pathname);

      navKeyRef.current = location.key;
      prevLocationRef.current = location;

  if (inShopBrowse || openingProduct) {
        // Suppress overlay for in-shop browsing (e.g., category changes)
        setVisible(false);
        setPhase("idle");
        sinceRef.current = Date.now();
        return;
      }

      sinceRef.current = Date.now();
      setVisible(true);
      setPhase("entering");
    } else {
      // Update prev ref to latest location even if key didn't change (defensive)
      prevLocationRef.current = location;
    }
  }, [location]);

  // Hide overlay when min duration passed and nothing pending
  useEffect(() => {
    let rafId;
    let timeoutId;
    let exitTimeoutId;

    const tryHide = () => {
      const elapsed = Date.now() - sinceRef.current;
      if (elapsed >= minDuration && pendingCount === 0) {
        // run exit animation then unmount
        setPhase("exiting");
        exitTimeoutId = setTimeout(() => setVisible(false), exitDuration);
      } else if (pendingCount === 0) {
        // wait remaining time
        const remaining = Math.max(0, minDuration - elapsed);
        timeoutId = setTimeout(() => {
          setPhase("exiting");
          exitTimeoutId = setTimeout(() => setVisible(false), exitDuration);
        }, remaining);
      } else {
        // pending tasks in progress; check again on next frame
        rafId = requestAnimationFrame(tryHide);
      }
    };

    if (visible && phase !== "exiting") {
      tryHide();
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      if (exitTimeoutId) clearTimeout(exitTimeoutId);
    };
  }, [visible, pendingCount, minDuration, exitDuration, phase]);

  // Note: We avoid locking body scroll to prevent sticky overflow issues across pages.

  if (!visible) return null;

  return (
    <div
      className={`app-loading-overlay${phase === "exiting" ? " is-exiting" : ""}`}
      role="status"
      aria-live="polite"
  >
      <div className="app-loading-spinner">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
};

export default RouteChangeLoader;
